import { CommonModule } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, effect, forwardRef, input, output, signal, untracked } from '@angular/core';
import { RouterModule } from '@angular/router';

import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

import { TaskCardComponent } from '@shared/_cards/task/task.component';
import { Guid, Task, TaskLink, TaskLinkTypeCss, TaskLinkTypeName, string2Color } from '@models';
import { StoreService } from '@services/store.service';

@Component({
    selector: 'vz-task-link-badge, [vz-task-link-badge]',
    template: `
        @if (shid(); as shortId) {
            <div class="vz-task-link-badge layout center"
                [class.c-p]="clickable() && accessible()"
                nz-tooltip
                [nzTooltipTrigger]="showTooltip() ? 'hover' : null"
                [nzTooltipColor]="'white'"
                [nzTooltipMouseEnterDelay]=".5"
                [nzTooltipTitle]="taskCardTpl"
                [nzTooltipOverlayClassName]="'ant-tt-p2'"
                [routerLink]="clickable() && accessible() ? ['/ui', activeOrgId, 'tasks', shortId] : null"
                [queryParamsHandling]="'merge'">
                @if (showText()) {
                    <span class="layout">{{ linkText() }}</span>
                }
                <b [style.margin-left.px]="showText() ? undefined : 0"><i [ngClass]="linkCss()"></i></b>
                <div [style.background-color]="color()">{{ shortId }}</div>
                @if (showDeleteButton() && accessible()) {
                    <i class="vz-btn-c-err vzi-cross fs-m" (click)="$event.stopImmediatePropagation(); onDelete.emit()"></i>
                }
            </div>
            <ng-template #taskCardTpl>
                <vz-task-card [taskId]="task()?.id" />
            </ng-template>
        }
    `,
    styles: [`
        @import 'theme';
        :host {
            .vz-task-link-badge {
                background-color: @bgColor;
                border-radius: 6px;
                overflow: hidden;
                & > span {
                    color: @textColorL;
                    background-color: @bgColorDD;
                    padding: 0 12px 0 8px;
                    border-top-left-radius: 6px;
                    border-bottom-left-radius: 6px;
                }
                & > b {
                    margin: 0 -4px;
                    background-color: @bgColor;
                    color: @primaryColor;
                    z-index: 1;
                    padding: 2px 5px;
                    border-radius: 50%;
                    border: 1px solid @shadowColor;
                    box-shadow: 0 0 0px 2px @bgColor;
                    display: flex;
                    width: 25px;
                    height: 25px;
                    justify-content: center;
                    align-items: center;
                }
                & > div {
                    color: @textColorInv;
                    padding: 0 8px 0 12px;
                    border-top-right-radius: 6px;
                    border-bottom-right-radius: 6px;
                }
            }
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, RouterModule, NzTooltipModule, forwardRef(() => TaskCardComponent)]
})
export class TaskLinkBadgeComponent {
    readonly taskId             = input<Guid>();
    readonly link               = input<TaskLink>();
    readonly showDeleteButton   = input(false, { transform: booleanAttribute });
    readonly clickable          = input(false, { transform: booleanAttribute });
    readonly showTooltip        = input(true, { transform: booleanAttribute });
    readonly showText           = input(true, { transform: booleanAttribute });
    readonly onDelete           = output<void>();

    readonly shid               = signal<Guid | undefined>(undefined);
    readonly task               = signal<Task | undefined>(undefined);
    readonly color              = signal<string>('#ccc');
    readonly title              = signal<string>('');
    readonly linkText           = signal<string>('связана с');
    readonly linkCss            = signal<string>('vzi-link vz-tl-related');
    readonly accessible         = signal<boolean>(false);

    activeOrgId: string = '';

    constructor(
        private _store: StoreService,
    ) {
        this.activeOrgId = _store.activeOrgId.getValue() || '';
        effect(() => {
            const taskId = this.taskId();
            const link = this.link();
            // console.log('=[TaskLinkBadgeComponent]===> constructor, taskId:', taskId, 'link:', link);
            if (link) {
                const to = link.taskFromId == taskId;
                this.task.set(new Task(to ? link.taskTo : link.taskFrom).parse({ flags: { seen: true } }));
                if (!link.taskFrom && !link.taskTo || (link.taskFrom && link.taskTo && link.taskFrom.projectId == link.taskTo.projectId)) {
                    this.accessible.set(true);
                }
                else {
                    const pm = this._store.getStateDirect('projects')?.items;
                    const pid = to ? link.taskTo?.projectId : link.taskFrom?.projectId;
                    this.accessible.set(!!pm && !!pid && !!pm[pid]);
                }
            }
        });
        effect(() => this.update(untracked(this.taskId), this.task(), untracked(this.link)));
    }

    update(taskId?: Guid, task?: Task, link?: TaskLink): void {
        if (!task || !link) {
            this.shid.set(undefined);
            this.color.set('#ccc');
            this.linkText.set('связана с');
            this.title.set('');
            return;
        }
        const ind = link.taskFromId == taskId ? 0 : 1;
        this.linkText.set(link.linkType != null && TaskLinkTypeName[link.linkType] ? TaskLinkTypeName[link.linkType][ind] : 'связана с');
        this.linkCss.set(link.linkType != null && TaskLinkTypeCss[link.linkType] ? TaskLinkTypeCss[link.linkType][ind] : 'vzi-link vz-tl-related');
        this.color.set(string2Color(task.projectId));
        this.shid.set(task.shortId || '???');
    }

}
